import { useCallback, useEffect, useRef, useState } from 'react';
import { formatDate } from '../../../lib/format';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, RotateCcw } from 'lucide-react';
import {
  Button, TextField, Alert, ConfirmDialog, Dialog, InfoTip, PhoneInput, phoneInputProblem,
  addressForm, addressPayload, useIdempotencyKey, useToast, cn,
  type AddressFormValue,
} from '@dru/ui';
import { ApiError } from '@dru/api-client';
import type { PartnerNetworkType } from '@dru/types';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { AddressFields } from '../../../components/AddressFields';
import { errorMessage } from '../../../lib/auth';
import { useUnsavedGuard } from '../../../lib/useUnsavedGuard';

/**
 * Registering a venue, in a dialog over the lab network list (owner's review,
 * 14 Oct 2026: the same as registering a company).
 *
 * It was moved to a page once because dismissing a modal threw away what was
 * typed. Since the form shrank to three short sections, and the draft is
 * written to local storage as it is typed, that no longer holds: the backdrop,
 * Escape and Cancel ask before closing a filled-in form, the draft survives a
 * closed tab or a crash, and the navigation guard catches leaving the page.
 */

// v2: the form lost its code, capacity, hours, website and emergency phone
// (owner's review, 14 Oct 2026); an older draft would restore fields that
// are no longer on the page.
const DRAFT_KEY = 'dru.admin.partner-register.draft.v2';

interface Draft {
  form: Record<string, string>;
  address: AddressFormValue;
  savedAt: string;
}

/**
 * Only what creating a partner needs (owner's review, 14 Oct 2026). The code
 * is made from the name, and capacity, hours, website and the emergency
 * number are set later, during setup on the partner's page.
 */
const EMPTY: Record<string, string> = {
  name: '', kind: 'HOSPITAL',
  contactName: '', contactPhone: '', contactAltPhone: '', contactEmail: '',
};

function readDraft(): Draft | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Draft;
    if (!parsed?.form || typeof parsed.form !== 'object') return null;
    return parsed;
  } catch {
    // A corrupt or unreadable draft is not worth an error on arrival — the
    // form still works, it just starts empty.
    return null;
  }
}

export function PartnerRegisterDialog({ onClose }: { onClose: () => void }) {
  const nav = useNavigate();
  const toast = useToast();
  const idem = useIdempotencyKey();

  // The type list is configuration, not a constant compiled into this form:
  // an administrator adds "Collection Centre" without a deployment.
  const types = useQuery<{ items: PartnerNetworkType[] }>(
    () => api.get('/partner-network-types'), [],
  );

  // Read once, before first paint, so the fields never flash empty and then
  // fill — which reads as the data having been lost.
  const [restored] = useState(() => readDraft());
  const [form, setForm] = useState<Record<string, string>>(restored?.form ?? EMPTY);
  const [address, setAddress] = useState<AddressFormValue>(
    restored?.address ?? addressForm(null),
  );
  const [savedAt, setSavedAt] = useState<string | null>(restored?.savedAt ?? null);
  const [resumed, setResumed] = useState(!!restored);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [discarding, setDiscarding] = useState(false);
  const [leaving, setLeaving] = useState(false);

  // Set once the record exists, so leaving afterwards is not challenged and
  // the draft does not outlive what it was a draft of.
  const submitted = useRef(false);

  // Two choices on the form; each maps to the catalogue's own entry so the
  // venue still carries a described type (the first active one that behaves
  // as a hospital, or as a laboratory).
  const typeFor = (kind: string) => {
    const items = types.data?.items ?? [];
    const behaviour = kind === 'LAB' ? 'LABORATORY' : 'HOSPITAL';
    return items.find((t) => t.code === (kind === 'LAB' ? 'PATHOLOGY_LAB' : 'HOSPITAL'))
      ?? items.find((t) => t.behaviour === behaviour) ?? null;
  };
  const touched = hasContent(form, address);

  // Written on a timer rather than on every keystroke: a draft is a safety
  // net, not a document, and a synchronous write per character in a form this
  // size is wasted work.
  useEffect(() => {
    if (!touched || submitted.current) return;
    const t = setTimeout(() => {
      try {
        const at = new Date().toISOString();
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ form, address, savedAt: at }));
        setSavedAt(at);
      } catch {
        // Private browsing, or a full quota. The form still submits; only the
        // safety net is missing, and saying so on every keystroke would be
        // noise. The navigation guard still applies.
      }
    }, 600);
    return () => clearTimeout(t);
  }, [form, address, touched]);

  useUnsavedGuard(touched && !submitted.current);

  const clearDraft = useCallback(() => {
    try { localStorage.removeItem(DRAFT_KEY); } catch { /* nothing to clear */ }
    setSavedAt(null);
  }, []);

  async function submit() {
    setBusy(true); setError(null); setFieldErrors({});
    try {
      const networkType = typeFor(form.kind);
      const created = await api.post<{ id: string }>('/partners', {
        name: form.name.trim(),
        type: form.kind === 'LAB' ? 'LABORATORY' : 'HOSPITAL',
        ...(networkType ? { networkTypeId: networkType.id } : {}),
        ...addressPayload(address),
        ...textFields(form, ['contactName', 'contactPhone', 'contactAltPhone', 'contactEmail']),
      }, idem.key());
      idem.rotate();
      // Order matters: mark submitted before navigating, or the guard fires on
      // the way out of a form that succeeded.
      submitted.current = true;
      clearDraft();
      toast.toast('success', `${form.name} registered. Finish its setup step by step on this page.`);
      nav(`/admin/partners/${created.id}`);
    } catch (e) {
      if (e instanceof ApiError) setFieldErrors(e.fieldErrors);
      setError(errorMessage(e));
      setBusy(false);
    }
  }

  // What still stops the submit, in words — said beside the button rather
  // than left to a greyed-out button nobody can explain.
  const phoneProblem = phoneInputProblem(form.contactPhone);
  const altProblem = phoneInputProblem(form.contactAltPhone);
  const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.contactEmail.trim());
  const missing = [
    form.name.trim().length < 2 && 'a name',
    !emailOk && 'an email',
    (phoneProblem || altProblem) && 'a valid phone number',
  ].filter(Boolean) as string[];
  const incomplete = missing.length > 0;

  /**
   * Leaving deliberately. The draft stays on disk either way — "leave" here
   * means "stop filling this in for now", not "throw it away", and the list
   * offers the way back. Discarding is a separate, explicit act.
   */
  function leave() {
    if (busy) return;
    if (touched && !submitted.current) { setLeaving(true); return; }
    onClose();
  }

  return (
    <Dialog
      title="Register a network partner"
      info={<>
        Add a hospital, diagnostic centre or laboratory. It starts as <strong className="font-medium">Awaiting review</strong> and
        cannot receive cases until the tests it can run are declared and it is approved.
      </>}
      onClose={leave}
      wide
      footer={(
        <div className="flex w-full flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {savedAt && (
              <span className="inline-flex items-center gap-1.5 text-caption text-neutral-500">
                <CheckCircle2 className="h-3.5 w-3.5 text-success-600" />
                Draft saved {relativeTime(savedAt)}
              </span>
            )}
            {resumed && (
              <button
                type="button"
                onClick={() => setDiscarding(true)}
                className="tap-area ml-2 inline-flex items-center gap-1 text-caption font-medium text-neutral-500 hover:text-danger-600 transition-colors"
                title="Clear the draft and start with an empty form"
              >
                <RotateCcw aria-hidden className="h-3 w-3" />
                Start over
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center justify-end gap-3">
            {incomplete && (
              <span id="register-missing" className="text-caption text-neutral-600">
                Still needed: {missing.join(', ')}.
              </span>
            )}
            <Button variant="secondary" onClick={leave} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={submit} loading={busy} disabled={incomplete} aria-describedby={incomplete ? 'register-missing' : undefined}>
              Register partner
            </Button>
          </div>
        </div>
      )}
    >
      <div className="space-y-6 pt-3">
        {resumed && (
          <Alert tone="info">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span>
                Restored unsubmitted draft
                {savedAt ? <> — saved {relativeTime(savedAt)}</> : null}.
              </span>
              <button
                type="button"
                onClick={() => setDiscarding(true)}
                className="text-caption font-semibold text-primary-700 hover:underline"
              >
                Start over
              </button>
            </div>
          </Alert>
        )}

        {/* Section 1: the partner */}
        <Section title="Partner">
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="Lab or hospital name"
              value={form.name}
              required
              autoFocus
              placeholder="e.g. Sahyadri Super Speciality Hospital"
              error={fieldErrors.name}
              onChange={(v) => setForm({ ...form, name: v })}
            />
            {/* Spaced and labelled like the field beside it, so both sit on one line. */}
            <div className="space-y-1.5">
              <span className="block text-caption text-neutral-700">Partner type</span>
              <div role="radiogroup" aria-label="Partner type" className="grid grid-cols-2 gap-2">
                {([['HOSPITAL', 'Hospital'], ['LAB', 'Lab']] as const).map(([k, label]) => (
                  <button
                    key={k}
                    type="button"
                    role="radio"
                    aria-checked={form.kind === k}
                    onClick={() => setForm({ ...form, kind: k })}
                    className={cn(
                      'h-10 rounded-input border text-body font-medium transition-colors max-md:h-11',
                      form.kind === k
                        ? 'border-primary-600 bg-primary-50 text-primary-700'
                        : 'border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50',
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </Section>

        {/* Section 2: contact. Hours, capacity and other numbers are set up later. */}
        <Section title="Contact">
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="Contact person"
              value={form.contactName}
              placeholder="e.g. Dr. Rajesh Sharma / Front Desk"
              error={fieldErrors.contactName}
              onChange={(v) => setForm({ ...form, contactName: v })}
            />
            <TextField
              label="Email"
              value={form.contactEmail}
              type="email"
              required
              placeholder="e.g. desk@sahyadri.com"
              error={fieldErrors.contactEmail}
              onChange={(v) => setForm({ ...form, contactEmail: v })}
            />
            <PhoneInput
              label="Mobile number"
              value={form.contactPhone}
              error={fieldErrors.contactPhone ?? (form.contactPhone ? phoneProblem ?? undefined : undefined)}
              onChange={(v) => setForm({ ...form, contactPhone: v })}
            />
            <PhoneInput
              label="Alternate mobile (optional)"
              value={form.contactAltPhone}
              error={fieldErrors.contactAltPhone ?? (form.contactAltPhone ? altProblem ?? undefined : undefined)}
              onChange={(v) => setForm({ ...form, contactAltPhone: v })}
            />
          </div>
        </Section>

        {/* Section 3: the address, last: it carries the map. */}
        <Section
          title="Address"
          hint="Becomes the partner's first branch. Type the PIN code to fill the rest; the map pin follows the address and can be dragged."
        >
          <AddressFields value={address} onChange={setAddress} errors={fieldErrors} hideLandmark autoLocate />
        </Section>

        {error && <Alert tone="danger">{error}</Alert>}

        {leaving && (
          <ConfirmDialog
            title="Close without registering?"
            what="This partner has not been registered yet."
            affects="What you have typed is saved on this device, and this form will offer it back the next time you open it."
            confirmLabel="Close"
            onConfirm={() => { setLeaving(false); onClose(); }}
            onCancel={() => setLeaving(false)}
          />
        )}

        {discarding && (
          <ConfirmDialog
            title="Start over?"
            what="The details saved on this device will be cleared and the form will be empty."
            confirmLabel="Clear the draft"
            tone="danger"
            onConfirm={() => {
              clearDraft();
              setForm(EMPTY);
              setAddress(addressForm(null));
              setResumed(false);
              setDiscarding(false);
            }}
            onCancel={() => setDiscarding(false)}
          />
        )}
      </div>
    </Dialog>
  );
}

/** A heading and its fields; the dialog is already the card. */
function Section({ title, hint, children }: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="mb-3 flex items-center gap-1">
        <h3 className="text-body font-semibold text-neutral-900">{title}</h3>
        {hint && <InfoTip label={`About ${title.toLowerCase()}`}>{hint}</InfoTip>}
      </div>
      {children}
    </section>
  );
}

/**
 * Whether anything worth protecting has been typed.
 *
 * The type field and the address country start populated, so they are
 * compared against their starting values; otherwise an untouched form would
 * be dirty and every close of it challenged.
 */
function hasContent(form: Record<string, string>, address: AddressFormValue): boolean {
  for (const [k, v] of Object.entries(form)) {
    if (EMPTY[k] === undefined) continue;
    if (v.trim() !== EMPTY[k]) return true;
  }
  // Against the empty address, not blank: it starts with the country filled in.
  const blank = addressForm(null) as unknown as Record<string, unknown>;
  return Object.entries(address).some(([k, v]) => typeof v === 'string' && v.trim() !== String(blank[k] ?? ''));
}

function relativeTime(iso: string): string {
  const seconds = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (seconds < 45) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  return formatDate(iso);
}

/** Non-empty text fields only: the API treats a blank string as a value. */
function textFields(form: Record<string, string>, keys: readonly string[]) {
  const out: Record<string, string> = {};
  for (const k of keys) if (form[k]?.trim()) out[k] = form[k].trim();
  return out;
}
